import { createContext, useContext, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";
import T from "../theme/colors";

const ToastContext = createContext(null);

const ICONS = {
  success: { icon: CheckCircle, color: T.success, bg: T.successBg, border: T.successBdr },
  error:   { icon: XCircle,     color: T.danger,  bg: T.dangerBg,  border: "#FCA5A5"    },
  warn:    { icon: AlertTriangle,color: T.warn,    bg: T.warnBg,    border: T.warnBdr    },
  info:    { icon: Info,         color: T.info,    bg: T.infoBg,    border: T.infoBdr    },
};

function ToastItem({ toast, onClose }) {
  const { icon: Icon, color, bg, border } = ICONS[toast.type] || ICONS.info;

  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        padding: "12px 14px 12px 12px",
        background: bg, border: `1px solid ${border}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 10,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
        maxWidth: 400, width: "100%",
        animation: "slideIn 0.25s ease",
        fontFamily: "var(--font-ui)",
      }}
    >
      <Icon size={16} style={{ color, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text0, marginBottom: 2 }}>
            {toast.title}
          </div>
        )}
        <div style={{ fontSize: 12, color: T.text1, lineHeight: 1.5, wordBreak: "break-word" }}>
          {toast.message}
        </div>
      </div>
      <button
        onClick={() => onClose(toast.id)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 2, color: T.text2, display: "flex", flexShrink: 0,
          borderRadius: 4, transition: "color 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.color = T.text0}
        onMouseLeave={e => e.currentTarget.style.color = T.text2}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timerRef.current[id]);
    setToasts(p => p.filter(t => t.id !== id));
  }, []);

  const show = useCallback((message, type = "info", title = null, duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p.slice(-4), { id, message, type, title }]); // max 5 toasts
    if (duration > 0) {
      timerRef.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Bridge for useMutation (avoids circular import)
  if (typeof window !== "undefined") window.__garmentToast = { success: (m,t) => show(m,"success",t), error: (m,t) => show(m,"error",t,8000), warn: (m,t) => show(m,"warn",t), info: (m,t) => show(m,"info",t) };

  const toast = {
    success: (msg, title) => show(msg, "success", title),
    error:   (msg, title) => show(msg, "error",   title, 8000), // errors stay longer
    warn:    (msg, title) => show(msg, "warn",     title),
    info:    (msg, title) => show(msg, "info",     title),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <>
          <style>{`
            @keyframes slideIn {
              from { transform: translateX(110%); opacity: 0; }
              to   { transform: translateX(0);    opacity: 1; }
            }
          `}</style>
          <div style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 99999,
            display: "flex", flexDirection: "column", gap: 10,
            pointerEvents: "none",
          }}>
            {toasts.map(t => (
              <div key={t.id} style={{ pointerEvents: "all" }}>
                <ToastItem toast={t} onClose={dismiss} />
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}